import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { GlassCard } from '../../components/common/GlassCard';
import { toast } from 'sonner';
import { Building2, Lock, ArrowLeft, CheckCircle, AlertTriangle, Eye, EyeOff, RefreshCw } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState('');
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: '',
    color: '',
  });

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenError('No reset token provided');
        setVerifying(false);
        return;
      }

      try {
        const response = await authAPI.verifyResetToken(token);
        if (response.data.valid) {
          setTokenValid(true);
          setEmail(response.data.email || '');
        } else {
          setTokenError(response.data.error || 'Invalid or expired token');
        }
      } catch (error) {
        setTokenError('Unable to verify token. It may have expired.');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  // Check password strength
  useEffect(() => {
    const password = formData.password;
    let score = 0;
    
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;
    
    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-lime-500', 'bg-green-500'];
    
    setPasswordStrength({
      score,
      label: password.length > 0 ? labels[Math.min(score, 4)] : '',
      color: password.length > 0 ? colors[Math.min(score, 4)] : '',
    });
  }, [formData.password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);

    try {
      await authAPI.resetPassword(token, formData.password);
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
      if (message.includes('expired') || message.includes('Invalid')) {
        setTokenValid(false);
        setTokenError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 to-slate-950" />
        <div className="relative z-10 text-center">
          <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4" data-testid="reset-password-page">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1676287734314-b7616c3c5e5a?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 to-slate-950" />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <Building2 className="w-10 h-10 text-emerald-500" />
            <span className="text-2xl font-bold text-gradient-gold">Sam Rumah</span>
          </Link>
          <h1 className="text-2xl font-bold text-white">
            {success ? 'Password Reset!' : tokenValid ? 'Create New Password' : 'Link Expired'}
          </h1>
          <p className="text-slate-400 mt-1">
            {success 
              ? 'Your password has been changed successfully' 
              : tokenValid 
                ? 'Enter your new password below'
                : 'This password reset link is no longer valid'}
          </p>
        </div>

        <GlassCard>
          {success ? (
            // Success state
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">All Done!</h3>
              <p className="text-slate-400 text-sm mb-6">
                Your password has been reset successfully. You can now log in with your new password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-emerald-500 hover:bg-emerald-600"
                data-testid="go-to-login"
              >
                Go to Login
              </Button>
            </div>
          ) : !tokenValid ? (
            // Invalid token state
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Link Invalid</h3>
              <p className="text-slate-400 text-sm mb-2">
                {tokenError || 'This password reset link has expired or is invalid.'}
              </p>
              <p className="text-slate-500 text-xs mb-6">
                Password reset links expire after 1 hour for security reasons.
              </p>
              <div className="space-y-3">
                <Link to="/forgot-password">
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-600">
                    Request New Reset Link
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            // Password form
            <form onSubmit={handleSubmit} className="space-y-4">
              {email && (
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-slate-400 text-xs">Resetting password for</p>
                  <p className="text-emerald-400 text-sm font-medium">{email}</p>
                </div>
              )}
              
              <div>
                <Label htmlFor="password" className="text-slate-300">New Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white"
                    required
                    minLength={8}
                    data-testid="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password strength indicator */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full ${
                            i <= passwordStrength.score ? passwordStrength.color : 'bg-slate-700'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-slate-400">
                      Password strength: <span className={`${passwordStrength.score >= 3 ? 'text-green-400' : 'text-orange-400'}`}>{passwordStrength.label}</span>
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white"
                    required
                    minLength={8}
                    data-testid="confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password match indicator */}
                {formData.confirmPassword && (
                  <p className={`text-xs mt-1 ${
                    formData.password === formData.confirmPassword ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {formData.password === formData.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}
              </div>

              <div className="bg-slate-800/30 rounded-lg p-3 text-xs text-slate-400">
                <p className="font-medium text-slate-300 mb-1">Password requirements:</p>
                <ul className="space-y-0.5">
                  <li className={formData.password.length >= 8 ? 'text-green-400' : ''}>• At least 8 characters</li>
                  <li className={/[a-z]/.test(formData.password) && /[A-Z]/.test(formData.password) ? 'text-green-400' : ''}>• Upper & lowercase letters</li>
                  <li className={/\d/.test(formData.password) ? 'text-green-400' : ''}>• At least one number</li>
                  <li className={/[^a-zA-Z0-9]/.test(formData.password) ? 'text-green-400' : ''}>• Special character (optional)</li>
                </ul>
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-500 hover:bg-emerald-600 btn-glow"
                disabled={loading || formData.password !== formData.confirmPassword || formData.password.length < 8}
                data-testid="reset-password-submit"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>

              <div className="text-center pt-2">
                <Link 
                  to="/login" 
                  className="text-slate-400 text-sm hover:text-emerald-400 inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </GlassCard>
      </div>
    </div>
  );
};

export default ResetPassword;
